import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { skipToken, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { trpc } from '@/lib/trpc';
import { WorldcooLogo } from '@worldcoo/ui/custom/worldcoo-logo';
import { toast } from '@/lib/toast';
import { NgoLanguageSelect } from './-components/ngo-language-select';
import { useState } from 'react';
import { navigateWithViewTransition } from '@/lib/view-transition-utils';
import { forgetLegalRepresentativeNotice } from './-components/legal-representative-banner';
import { forgetContractSignatureNotice } from './-components/contract-signature-banner';
import { clearPersistedQueryCache } from '@/lib/query-persistence';
import { ngoPostLoginHref } from '@/lib/ngo-session';
import * as v from 'valibot';

export const Route = createFileRoute('/ngos/select-ngo')({
	validateSearch: v.object({
		redirect: v.fallback(v.optional(v.string()), undefined),
	}),
	component: SelectNgoPage,
});

function SelectNgoPage() {
	const { t } = useLingui();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { redirect: redirectPath } = Route.useSearch();

	// A cached `{ authenticated: false }` from a prior visit must not beat the magic link's new cookies.
	const session = useQuery({
		...trpc.auth.validateSelectionSession.queryOptions(),
		staleTime: 0,
		refetchOnMount: 'always',
	});
	const isAdmin = session.data?.authenticated === true && session.data.mode === 'admin';

	const ngoList = useQuery(
		trpc.auth.listSelectableNgos.queryOptions(session.data?.authenticated ? skipToken : undefined),
	);

	const [searchQuery, setSearchQuery] = useState('');

	const { mutate: selectNgo, isPending } = useMutation(
		trpc.auth.selectNgo.mutationOptions({
			onSuccess: (result) => {
				if (result.success) {
					clearPersistedQueryCache(queryClient);
					forgetLegalRepresentativeNotice();
					forgetContractSignatureNotice();
					// `href`, not `to`: the target is a public path with its query and anchor, which only `href` runs through the host rewrite.
					navigateWithViewTransition(
						() => navigate({ href: ngoPostLoginHref(redirectPath) }),
						() => !!document.querySelector('[data-slot="sidebar"]'),
					);
				} else if (result.error === 'not_found') {
					toast.error(t`No NGO found`);
				} else {
					toast.error(t`Unauthorized`);
				}
			},
			onError: () => {
				toast.error(t`Something went wrong. Please try again.`);
			},
		}),
	);

	const filteredNgos =
		ngoList.data?.success && ngoList.data.ngos
			? ngoList.data.ngos.filter((ngo) => {
					if (!searchQuery) return true;
					const q = searchQuery.toLowerCase();
					return (
						ngo.name?.toLowerCase().includes(q) ||
						ngo.email?.toLowerCase().includes(q) ||
						ngo.id?.toLowerCase().includes(q)
					);
				})
			: [];

	const description = isAdmin
		? t`Pick an NGO from the list to access`
		: t`Your email has access to multiple NGOs. Choose which one you want to log in to.`;
	const emptyMessage = isAdmin ? t`No NGOs found` : t`No NGOs matched. Contact support if this looks wrong.`;

	return (
		<div
			data-page="select-ngo"
			className="relative flex min-h-screen bg-white"
			style={{ viewTransitionName: 'ngo-main-content' }}
		>
			<div className="absolute bottom-2 left-2 px-4 py-2 md:bottom-6 md:left-6 md:px-12 md:py-6">
				<NgoLanguageSelect />
			</div>

			<div className="flex flex-1 flex-col p-6 sm:p-10 lg:p-20">
				<WorldcooLogo accentClassName="text-wc-orange" className="h-12 w-auto self-start md:h-20" />

				<div className="mt-8 flex flex-1 flex-col pb-16 md:mt-12 md:pb-0">
					<div className="flex w-full max-w-150 flex-col gap-6">
						<div className="flex flex-col gap-2 text-[#35433f]">
							<h1 className="text-2xl leading-tight font-medium tracking-[-0.2px] md:text-[32px] md:leading-9.5">{t`Select NGO`}</h1>
							<p className="text-base leading-normal text-[#6b7280]">
								{t`Logged in as`}{' '}
								<span className="font-medium text-[#35433f]">
									{session.data?.authenticated ? session.data.email : '...'}
								</span>
							</p>
							<p className="text-sm leading-normal text-[#6b7280]">{description}</p>
						</div>

						<div className="flex flex-col gap-3">
							{isAdmin && (
								<input
									type="text"
									placeholder={t`Search by name, email, or ID...`}
									value={searchQuery}
									onChange={(e) => setSearchQuery(e.target.value)}
									className="focus:border-primary focus:ring-primary h-10 w-full rounded-md border border-[#d1d5db] px-3 text-sm outline-none focus:ring-1"
								/>
							)}

							<div className="max-h-100 min-h-60 overflow-y-auto rounded-lg border border-[#e5e7eb]">
								{ngoList.isPending && (
									<div className="flex flex-col">
										{Array.from({ length: 5 }).map((_, i) => (
											<div
												// oxlint-disable-next-line react/no-array-index-key
												key={`skeleton-${i}`}
												className="flex items-center gap-3 border-b border-[#f3f4f6] px-4 py-3 last:border-b-0"
											>
												<div className="size-8 shrink-0 animate-pulse rounded-lg bg-[#e5e7eb]" />
												<div className="flex flex-1 flex-col gap-1.5">
													<div className="h-3.5 w-32 animate-pulse rounded bg-[#e5e7eb]" />
													<div className="h-3 w-48 animate-pulse rounded bg-[#e5e7eb]" />
												</div>
											</div>
										))}
									</div>
								)}
								{!ngoList.isPending && filteredNgos.length === 0 && (
									<p className="p-4 text-center text-sm text-[#6b7280]">{emptyMessage}</p>
								)}
								{filteredNgos.map((ngo) => (
									<NgoRow key={ngo.id} ngo={ngo} isPending={isPending} onSelect={(ngoId) => selectNgo({ ngoId })} />
								))}
							</div>
						</div>

						<button
							type="button"
							onClick={() => navigate({ to: '/ngos/login' })}
							className="mt-2 w-fit text-sm text-[#6b7280] underline hover:text-[#35433f]"
						>
							{t`Back to login`}
						</button>
					</div>
				</div>
			</div>
		</div>
	);
}

function NgoRow(props: {
	ngo: { id: string; name: string; email: string; type: 'draft' | 'active' };
	isPending: boolean;
	onSelect: (ngoId: string) => void;
}) {
	const { t } = useLingui();

	return (
		<button
			type="button"
			disabled={props.isPending}
			onClick={() => props.onSelect(props.ngo.id)}
			className="flex w-full items-center gap-3 border-b border-[#f3f4f6] px-4 py-3 text-left transition-colors last:border-b-0 hover:bg-[#fff7ed] disabled:opacity-50"
		>
			<div className="text-primary flex size-8 shrink-0 items-center justify-center rounded-lg border border-[#e5e7eb] bg-white text-xs font-bold">
				{props.ngo.name?.charAt(0)?.toUpperCase() ?? 'N'}
			</div>
			<div className="flex min-w-0 flex-1 flex-col">
				<span className="text-wc-black truncate text-sm font-medium">{props.ngo.name || t`Unnamed NGO`}</span>
				<span className="truncate text-xs text-[#6b7280]">{props.ngo.email}</span>
			</div>
			{props.ngo.type === 'draft' && (
				<span className="shrink-0 rounded-full bg-[#fef3c7] px-2 py-0.5 text-xs font-medium text-[#92400e]">{t`Draft`}</span>
			)}
		</button>
	);
}
